import React, { useState, useEffect } from 'react';
import PlayerService from '../../services/PlayerService';
import './ReferralNetworkMap.css';

const ReferralNetworkMap = ({ onClose, currentUserId }) => {
  const [networkData, setNetworkData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(currentUserId);

  useEffect(() => {
    fetchReferralNetwork();
  }, []);

  const fetchReferralNetwork = async () => {
    try {
      setLoading(true);

      const allPlayers = await PlayerService.getAllPlayers();
      if (!allPlayers || allPlayers.length === 0) {
        setError('No player data found');
        setLoading(false);
        return;
      }

      // Build tree structure
      const playerMap = {};
      allPlayers.forEach(player => {
        playerMap[player.id] = {
          ...player,
          children: []
        };
      });

      // Find root nodes and build hierarchy
      const rootNodes = [];
      allPlayers.forEach(player => {
        if (player.referredBy && playerMap[player.referredBy]) {
          playerMap[player.referredBy].children.push(playerMap[player.id]);
        } else {
          // Root node (no referrer)
          rootNodes.push(playerMap[player.id]);
        }
      });

      // Sort by qubits
      const sortByQubits = (a, b) => (b.qubits || 0) - (a.qubits || 0);
      rootNodes.sort(sortByQubits);
      rootNodes.forEach(node => sortChildren(node));

      setNetworkData(rootNodes);

      // Auto-expand nodes leading to current user
      if (currentUserId) {
        expandPathToUser(currentUserId, playerMap);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching referral network:', error);
      setError('Failed to load referral network');
      setLoading(false);
    }
  };

  const sortChildren = (node) => {
    if (node.children && node.children.length > 0) {
      node.children.sort((a, b) => (b.qubits || 0) - (a.qubits || 0));
      node.children.forEach(child => sortChildren(child));
    }
  };

  const expandPathToUser = (userId, playerMap) => {
    const expanded = new Set();
    let currentId = userId;

    // Walk up the referral chain
    while (currentId) {
      const player = playerMap[currentId];
      if (!player) break;

      if (player.referredBy) {
        expanded.add(player.referredBy);
        currentId = player.referredBy;
      } else {
        break;
      }
    }

    setExpandedNodes(expanded);
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const getRankColor = (rank) => {
    const rankColors = {
      '0': "#A9A9A9",
      '1': "#00BFFF",
      '2': "#9370DB",
      '3': "#FFD700",
      '4': "#008080",
      '5': "#8A2BE2",
      '6': "#FD6907"
    };
    return rankColors[rank] || "#33ff33";
  };

  const getRankName = (rank) => {
    const rankNames = {
      '0': "Signal Seeker",
      '1': "Void Aligned",
      '2': "Sentient",
      '3': "Frequency Specialist",
      '4': "Echo Bound",
      '5': "Resonance Manipulator",
      '6': "VOID"
    };
    return rankNames[rank] || "Unknown";
  };

  const countTotalDescendants = (node) => {
    if (!node.children || node.children.length === 0) return 0;

    let count = node.children.length;
    node.children.forEach(child => {
      count += countTotalDescendants(child);
    });
    return count;
  };

  const matchesSearch = (node, term) => {
    if (!term) return true;
    const lowerTerm = term.toLowerCase();
    return (
      node.discordUsername?.toLowerCase().includes(lowerTerm) ||
      node.id?.toLowerCase().includes(lowerTerm)
    );
  };

  const hasMatchingDescendant = (node, term) => {
    if (matchesSearch(node, term)) return true;
    if (!node.children) return false;
    return node.children.some(child => hasMatchingDescendant(child, term));
  };

  const filterNodes = (nodes, term) => {
    if (!term) return nodes;
    return nodes.filter(node => hasMatchingDescendant(node, term));
  };

  const renderNode = (node, level = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const isCurrentUser = node.id === currentUserId;
    const isSelected = node.id === selectedUserId;
    const descendantCount = countTotalDescendants(node);

    if (searchTerm && !matchesSearch(node, searchTerm) && !hasMatchingDescendant(node, searchTerm)) {
      return null;
    }

    return (
      <div key={node.id} className="tree-node-container">
        <div
          className={`tree-node ${isCurrentUser ? 'current-user' : ''} ${isSelected ? 'selected' : ''}`}
          style={{
            paddingLeft: `${level * 24 + 12}px`
          }}
          onClick={() => setSelectedUserId(node.id)}
        >
          {/* Tree connector line */}
          {level > 0 && (
            <div className="tree-line" style={{ left: `${level * 24 - 12}px` }} />
          )}

          {/* Expand/collapse button */}
          <div className="node-toggle" onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) toggleNode(node.id);
          }}>
            {hasChildren ? (
              isExpanded ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              )
            ) : (
              <div style={{ width: '14px', height: '14px' }} />
            )}
          </div>

          {/* Folder/User icon */}
          <div className="node-icon">
            {hasChildren ? (
              isExpanded ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={getRankColor(node.rank)} strokeWidth="2">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={getRankColor(node.rank)} strokeWidth="2">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                  <line x1="12" y1="11" x2="12" y2="17"></line>
                  <line x1="9" y1="14" x2="15" y2="14"></line>
                </svg>
              )
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={getRankColor(node.rank)} strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            )}
          </div>

          {/* User info */}
          <div className="node-content">
            <div className="node-name">
              {node.discordUsername || 'Unknown User'}
              {isCurrentUser && <span className="you-badge">YOU</span>}
              {node.isAdmin && <span className="admin-badge-small">ADMIN</span>}
            </div>
            <div className="node-stats">
              <span className="node-rank" style={{ color: getRankColor(node.rank) }}>
                R{node.rank}
              </span>
              <span className="node-qubits">{(node.qubits || 0).toLocaleString()} qubits</span>
              {hasChildren && (
                <span className="node-referrals">
                  {node.children.length} direct · {descendantCount} total
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Render children if expanded */}
        {hasChildren && isExpanded && (
          <div className="tree-children">
            {node.children.map(child => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const expandAll = () => {
    const allIds = new Set();
    const collectIds = (nodes) => {
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          allIds.add(node.id);
          collectIds(node.children);
        }
      });
    };
    collectIds(networkData);
    setExpandedNodes(allIds);
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  if (loading) {
    return (
      <div className="referral-network-modal">
        <div className="modal-content loading">
          <div className="loading-text">LOADING REFERRAL MAP...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="referral-network-modal">
        <div className="modal-content error">
          <div className="error-text">{error}</div>
          <button onClick={onClose} className="close-button">CLOSE</button>
        </div>
      </div>
    );
  }

  const filteredData = filterNodes(networkData, searchTerm);

  return (
    <div className="referral-network-modal">
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-content tree-view">
        {/* Header */}
        <div className="modal-header">
          <h2>REFERRAL MAP</h2>
          <div className="header-controls">
            <button onClick={onClose} className="close-button">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="tree-controls">
          <div className="search-container">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#33ff33" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="tree-search-input"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="clear-search">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            )}
          </div>
          <div className="tree-actions">
            <button onClick={expandAll} className="tree-action-btn">
              EXPAND ALL
            </button>
            <button onClick={collapseAll} className="tree-action-btn">
              COLLAPSE ALL
            </button>
          </div>
        </div>

        {/* Tree */}
        <div className="tree-container">
          {filteredData.length > 0 ? (
            filteredData.map(node => renderNode(node, 0))
          ) : (
            <div className="tree-empty">
              {searchTerm ? 'No users found matching your search' : 'No referral data available'}
            </div>
          )}
        </div>

        {/* Stats Footer */}
        <div className="tree-footer">
          <span>Total Root Users: {networkData.length}</span>
          <span>|</span>
          <span>Showing: {filteredData.length} {searchTerm ? 'matching' : 'root'} users</span>
        </div>
      </div>
    </div>
  );
};

export default ReferralNetworkMap;
